import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { Brand } from '../components/Brand';
import { useAuth } from '../api/auth';
import { ApiError, apiFetch } from '../api/client';
import { Button, FormField, TextInput } from '../components/DesignSystem';

import type { LoginResult, SignupInvitationView } from '../api/types';

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

type InvitationState =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'ready'; invitation: SignupInvitationView };

interface FieldErrors {
  displayName?: string;
  password?: string;
  confirmPassword?: string;
  orgName?: string;
  orgSlug?: string;
}

/** Invitation-token signup: set your password + organisation, auto-login. */
export function SignupPage() {
  const { user, adoptSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [invitation, setInvitation] = useState<InvitationState>({ kind: 'loading' });
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvitation({ kind: 'invalid', message: 'This signup link is missing its token.' });
      return;
    }

    let cancelled = false;
    apiFetch<SignupInvitationView>(`/auth/invitations/${encodeURIComponent(token)}`)
      .then((result) => {
        if (cancelled) return;
        setInvitation({ kind: 'ready', invitation: result });
        setDisplayName((current) => current || (result.invitedName ?? ''));
        setOrgName((current) => current || (result.invitedName ?? ''));
        setOrgSlug((current) => current || slugify(result.invitedName ?? ''));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message =
          error instanceof ApiError && error.status === 410
            ? 'This invitation has expired or was already used. Ask for a new invite.'
            : error instanceof ApiError && error.status === 404
              ? 'This signup link is not valid. Check the link from your invitation email.'
              : 'Unable to reach the server — please try again.';
        setInvitation({ kind: 'invalid', message });
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (user !== null) {
    return <Navigate replace to="/dashboard" />;
  }

  function updateOrgName(value: string) {
    setOrgName(value);
    if (!slugEdited) {
      setOrgSlug(slugify(value));
    }
  }

  function validate(): boolean {
    const next: FieldErrors = {};
    if (displayName.trim().length === 0) {
      next.displayName = 'Enter your name';
    }
    if (password.length < 8) {
      next.password = 'Password must be at least 8 characters';
    }
    if (confirmPassword !== password) {
      next.confirmPassword = 'Passwords do not match';
    }
    if (orgName.trim().length === 0) {
      next.orgName = 'Enter your organisation name';
    }
    if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(orgSlug) || orgSlug.length < 2) {
      next.orgSlug = 'Lowercase letters, numbers, and hyphens only';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const result = await apiFetch<LoginResult>('/auth/signup', {
        method: 'POST',
        body: {
          token,
          displayName: displayName.trim(),
          password,
          orgName: orgName.trim(),
          orgSlug,
        },
      });
      adoptSession(result);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setFieldErrors({ orgSlug: 'That organisation URL name is taken — pick another' });
      } else if (error instanceof ApiError && error.status === 410) {
        setInvitation({
          kind: 'invalid',
          message: 'This invitation has expired or was already used. Ask for a new invite.',
        });
      } else {
        setFormError(
          error instanceof ApiError
            ? error.message
            : 'Unable to reach the server — please try again',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="App rfp-login">
      <section className="rfp-login__card">
        <Brand />
        <h1>Set up your organisation</h1>

        {invitation.kind === 'loading' && (
          <p className="rfp-login__intro">Checking your invitation…</p>
        )}

        {invitation.kind === 'invalid' && (
          <>
            <p className="rfp-login__error" role="alert">
              {invitation.message}
            </p>
            <p className="rfp-login__intro">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </>
        )}

        {invitation.kind === 'ready' && (
          <>
            <p className="rfp-login__intro">
              You&rsquo;re signing up as <strong>{invitation.invitation.email}</strong>. Choose your
              organisation name and password.
            </p>

            <form className="rfp-login__form" noValidate onSubmit={handleSubmit}>
              {formError && (
                <p className="rfp-login__error" role="alert">
                  {formError}
                </p>
              )}

              <FormField error={fieldErrors.displayName} htmlFor="signup-name" label="Your name">
                <TextInput
                  autoComplete="name"
                  id="signup-name"
                  invalid={fieldErrors.displayName !== undefined}
                  onChange={(event) => setDisplayName(event.target.value)}
                  value={displayName}
                />
              </FormField>

              <FormField
                error={fieldErrors.orgName}
                htmlFor="signup-org-name"
                label="Organisation display name"
              >
                <TextInput
                  id="signup-org-name"
                  invalid={fieldErrors.orgName !== undefined}
                  onChange={(event) => updateOrgName(event.target.value)}
                  placeholder="Acme Television"
                  value={orgName}
                />
              </FormField>

              <FormField
                error={fieldErrors.orgSlug}
                htmlFor="signup-org-slug"
                label="Organisation URL name"
              >
                <TextInput
                  id="signup-org-slug"
                  invalid={fieldErrors.orgSlug !== undefined}
                  onChange={(event) => {
                    setSlugEdited(true);
                    setOrgSlug(slugify(event.target.value));
                  }}
                  placeholder="acme-television"
                  value={orgSlug}
                />
              </FormField>

              <FormField error={fieldErrors.password} htmlFor="signup-password" label="Password">
                <TextInput
                  autoComplete="new-password"
                  id="signup-password"
                  invalid={fieldErrors.password !== undefined}
                  onChange={(event) => setPassword(event.target.value)}
                  type="password"
                  value={password}
                />
              </FormField>

              <FormField
                error={fieldErrors.confirmPassword}
                htmlFor="signup-confirm"
                label="Confirm password"
              >
                <TextInput
                  autoComplete="new-password"
                  id="signup-confirm"
                  invalid={fieldErrors.confirmPassword !== undefined}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  type="password"
                  value={confirmPassword}
                />
              </FormField>

              <Button disabled={isSubmitting} type="submit">
                {isSubmitting ? 'Creating your organisation…' : 'Create organisation'}{' '}
                <span aria-hidden="true">→</span>
              </Button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
